/**
 * Single source of truth for pool network identity.
 * Subgraph deployment IDs stay in `lib/uniswap/endpoints.ts` (Graph-specific).
 */

export type ChainDef = {
	slug: string;
	displayName: string;
	/** GeckoTerminal / CoinGecko on-chain network path segment; null if unused. */
	geckoNetwork: string | null;
	/** Shown in pool catalog filters and list. */
	inCatalog: boolean;
};

export const CHAINS: readonly ChainDef[] = [
	{ slug: "arbitrum", displayName: "Arbitrum", geckoNetwork: "arbitrum", inCatalog: true },
	{ slug: "avalanche", displayName: "Avalanche", geckoNetwork: null, inCatalog: false },
	{ slug: "base", displayName: "Base", geckoNetwork: "base", inCatalog: true },
	{ slug: "ethereum", displayName: "Ethereum", geckoNetwork: "eth", inCatalog: true },
	{ slug: "optimism", displayName: "Optimism", geckoNetwork: "optimism", inCatalog: true },
	{ slug: "polygon", displayName: "Polygon", geckoNetwork: null, inCatalog: false },
	{ slug: "robinhood", displayName: "Robinhood", geckoNetwork: "robinhood", inCatalog: true },
	{ slug: "unichain", displayName: "Unichain", geckoNetwork: "unichain", inCatalog: true },
] as const;

const BY_SLUG = new Map(CHAINS.map((c) => [c.slug, c]));
const BY_DISPLAY_LOWER = new Map(CHAINS.map((c) => [c.displayName.toLowerCase(), c]));

export function resolveChain(input: string | null | undefined): ChainDef | null {
	if (!input?.trim()) return null;
	const key = input.trim().toLowerCase();
	return BY_SLUG.get(key) ?? BY_DISPLAY_LOWER.get(key) ?? null;
}

export function getNetworkDisplayName(chain: string): string {
	const found = resolveChain(chain);
	if (found) return found.displayName;
	return chain.charAt(0).toUpperCase() + chain.slice(1);
}

/** Display name or slug → Uniswap app / internal URL slug. */
export function getUniswapNetworkSlug(network: string): string {
	return resolveChain(network)?.slug ?? network.trim().toLowerCase();
}

/** App slug or display name → GeckoTerminal network segment. */
export function getGeckoNetwork(chain: string): string | null {
	return resolveChain(chain)?.geckoNetwork ?? null;
}

/** True when the chain has a GeckoTerminal network mapping. */
export function hasGeckoNetwork(chain: string): boolean {
	return getGeckoNetwork(chain) != null;
}

/** Catalog / filter display names (stable order). */
export const SUPPORTED_POOL_NETWORKS = CHAINS.filter((c) => c.inCatalog).map(
	(c) => c.displayName,
) as readonly string[];

export type SupportedPoolNetwork = (typeof SUPPORTED_POOL_NETWORKS)[number];
